/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../contexts";
import { Box, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { USER_ROLES } from "../../utils/constants";
import {
  AltRouteOutlined,
  BarChartOutlined,
  Check,
  CloudOutlined,
  DarkModeOutlined,
  DescriptionOutlined,
  DesktopWindowsOutlined,
  ExpandLess,
  GroupOutlined,
  HistoryOutlined,
  InboxOutlined,
  LayersOutlined,
  LightModeOutlined,
  PublicOutlined,
  ShieldOutlined,
  VpnKey,
  ViewInAr,
} from "@mui/icons-material";
import DrawerSnapToggle from "../../components/DrawerSnapToggle";
import { Argo } from "../../icons";
import { useHasPermission } from "../../api/iam";
import useIamModal from "../IamModal/UseIamModal";
import Cache from "../../utils/Cache";
import { ColorModeContext, DARK_PALETTES, tokens } from "../../theme";

const RAIL_EXPANDED = 232;
const RAIL_COLLAPSED = 64;
// Below this the rail collapses on its own; the stored choice applies again above.
const AUTO_COLLAPSE_BELOW = 1180;
const COLLAPSE_KEY = "sidebar-collapsed";

// Rail palette, resolved from tokens(paletteId) once per render.
const rail = (c) => ({
  ACTION: c.navActive,
  SURFACE: c.surface,
  SELECTED_TINT: c.selected,
  ROW_HOVER: c.ctlHoverBg,
  INK: c.ctlInkHover,
  ITEM_INK: c.ctlInk,
  MUTED: c.railMuted,
  HEADING: c.railHeading,
  RAIL_EDGE: c.railEdge,
  RULE: c.railRule,
  COLLAPSED_RULE: c.railRuleStrong,
  BUTTON_BORDER: c.ctl,
  BUTTON_BORDER_HOVER: c.ctlBorderHover,
  ON_DOT: c.onDot,
});

// Feather-style icons matching the portal design (convoy-portal.html).
const DatabaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
  </svg>
);

const PackageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
  </svg>
);

const GitPullRequestIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="18" r="3"></circle>
    <circle cx="6" cy="6" r="3"></circle>
    <path d="M13 6h3a2 2 0 0 1 2 2v7"></path>
    <line x1="6" y1="9" x2="6" y2="21"></line>
  </svg>
);

const ServerIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.45"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2.4" y="3" width="11.2" height="4" rx="1.2" />
    <rect x="2.4" y="9" width="11.2" height="4" rx="1.2" />
    <path d="M4.8 5h.01M4.8 11h.01" />
  </svg>
);

const MODE_OPTIONS = [
  { id: "light", label: "Light", Icon: LightModeOutlined },
  { id: "dark", label: "Dark", Icon: DarkModeOutlined },
  { id: "system", label: "System", Icon: DesktopWindowsOutlined },
];

const MOTION = {
  transition: "width 180ms ease-out",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
};

const styles = (c) => {
  const {
    ACTION: action,
    SURFACE: surface,
    SELECTED_TINT: selectedtint,
    ROW_HOVER: rowhover,
    INK: ink,
    ITEM_INK: itemink,
    MUTED: muted,
    HEADING: heading,
    RAIL_EDGE: railedge,
    RULE: rule,
    COLLAPSED_RULE: collapsedrule,
    BUTTON_BORDER: buttonborder,
    BUTTON_BORDER_HOVER: buttonborderhover,
    ON_DOT: ondot,
  } = rail(c);
  return {
    sidebar: (collapsed) => ({
      position: "relative",
      zIndex: 1,
      flex: "none",
      width: `${collapsed ? RAIL_COLLAPSED : RAIL_EXPANDED}px`,
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: surface,
      borderRight: `1px solid ${railedge}`,
      ...MOTION,
    }),
    toggle: {
      position: "absolute",
      top: "58px",
      right: "-13px",
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "26px",
      height: "26px",
      p: 0,
      border: `1px solid ${buttonborder}`,
      borderRadius: "999px",
      backgroundColor: surface,
      color: muted,
      boxShadow: "0 1px 4px rgba(16,24,40,.12)",
      cursor: "pointer",
      "&:hover": { color: action, borderColor: buttonborderhover },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "2px",
      },
    },
    header: (collapsed) => ({
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: "11px",
      p: collapsed ? "14px 0" : "14px",
      borderBottom: `1px solid ${rule}`,
    }),
    logo: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "34px",
      height: "34px",
      borderRadius: "9px",
      backgroundColor: action,
      color: "#FFFFFF",
    },
    brandText: { minWidth: 0, overflow: "hidden" },
    title: {
      fontSize: 14.5,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: ink,
      whiteSpace: "nowrap",
    },
    subtitle: { fontSize: 12, color: heading, whiteSpace: "nowrap" },

    // Only this region scrolls; the toggle button must not be clipped by the rail.
    // Collapsed, the scrollbar reserves space on both edges — taking it from the
    // right alone would push the icon column off the rail's centre line.
    nav: (collapsed) => ({
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarGutter: collapsed ? "stable both-edges" : "auto",
      p: collapsed ? "8px 0 12px" : "6px 0 12px",
    }),
    group: {
      p: "11px 14px 4px",
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: heading,
      whiteSpace: "nowrap",
    },
    groupRule: {
      height: "1px",
      m: "8px 14px 7px",
      backgroundColor: collapsedrule,
    },
    item: (active, collapsed) => ({
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: collapsed ? 0 : "11px",
      width: collapsed ? "40px" : "calc(100% - 16px)",
      height: "36px",
      m: collapsed ? "1px auto" : "1px 8px",
      px: collapsed ? 0 : "12px",
      border: "none",
      borderRadius: "6px",
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: active ? 600 : 500,
      textAlign: "left",
      textDecoration: "none",
      cursor: "pointer",
      userSelect: "none",
      backgroundColor: active ? selectedtint : "transparent",
      color: active ? action : collapsed ? muted : itemink,
      transition: "background-color 100ms, color 100ms",
      "&:hover": {
        backgroundColor: active ? selectedtint : rowhover,
        color: active ? action : ink,
      },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "-2px",
      },
      "& svg": {
        flexShrink: 0,
        fontSize: collapsed ? 18 : 17,
        width: collapsed ? 18 : 17,
        height: collapsed ? 18 : 17,
      },
    }),
    // Collapsed the label leaves the flow entirely: hiding it while it still
    // occupied its width pushed the icon off the centre of its box. `aria-label`
    // on the link keeps the name in the accessible tree either way.
    itemLabel: (collapsed) => ({
      display: collapsed ? "none" : "block",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      transition: "opacity 120ms ease-out",
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    }),

    backendRow: (collapsed) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "space-between",
      gap: "10px",
      m: collapsed ? "14px 0 0" : "14px 8px 0",
      p: collapsed ? "11px 0 0" : "9px 12px 0",
      borderTop: `1px solid ${rule}`,
    }),
    backendLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: 12.5,
      fontWeight: 500,
      color: itemink,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    backendIcon: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "36px",
      mt: "11px",
      p: 0,
      border: "none",
      borderRadius: "6px",
      backgroundColor: "transparent",
      color: muted,
      cursor: "pointer",
      "&:hover": { backgroundColor: rowhover, color: ink },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "-2px",
      },
      "& svg": { width: 18, height: 18 },
    },
    backendDot: (on) => ({
      position: "absolute",
      top: "7px",
      right: "9px",
      width: "7px",
      height: "7px",
      borderRadius: "999px",
      backgroundColor: on ? ondot : heading,
      boxShadow: `0 0 0 2px ${surface}`,
    }),

    // Anchors the appearance popup, which sits above the row.
    footer: (collapsed) => ({
      position: "relative",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: "10px",
      p: collapsed ? "12px 0" : "12px 14px",
      borderTop: `1px solid ${rule}`,
    }),
    // Expanded, the whole account row is the popup's trigger.
    accountButton: (open) => ({
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      m: 0,
      p: 0,
      border: "none",
      borderRadius: "6px",
      backgroundColor: open ? rowhover : "transparent",
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { backgroundColor: rowhover },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "2px",
      },
    }),
    accountChevron: (open) => ({
      flex: "none",
      ml: "auto",
      fontSize: 17,
      color: heading,
      transform: open ? "none" : "rotate(180deg)",
      transition: "transform 120ms ease",
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    }),
    menuPopup: {
      position: "absolute",
      left: "8px",
      right: "8px",
      bottom: "calc(100% + 6px)",
      zIndex: 3,
      p: "4px",
      border: `1px solid ${c.border}`,
      borderRadius: "8px",
      backgroundColor: c.surface,
      boxShadow: c.shadow,
    },
    menuOverline: {
      px: "8px",
      pt: "6px",
      pb: "4px",
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: c.muted,
    },
    menuRow: (selected) => ({
      width: "100%",
      height: "32px",
      display: "flex",
      alignItems: "center",
      gap: "9px",
      px: "8px",
      border: "none",
      borderRadius: "5px",
      backgroundColor: selected ? c.selected : "transparent",
      color: selected ? c.link : itemink,
      font: "inherit",
      fontSize: 12.5,
      fontWeight: selected ? 600 : 500,
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { backgroundColor: selected ? c.selected : rowhover },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "-2px",
      },
      "& svg": { flexShrink: 0 },
    }),
    menuRowIcon: { fontSize: 15 },
    menuRowCheck: { ml: "auto", fontSize: 14 },
    // Nested palette list, hung off a rule so it reads as a child of Dark.
    paletteWrap: {
      ml: "17px",
      pl: "7px",
      borderLeft: `2px solid ${c.border}`,
    },
    paletteOverline: {
      px: "8px",
      pt: "5px",
      pb: "3px",
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: c.muted,
    },
    paletteRow: (selected) => ({
      width: "100%",
      height: "30px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      px: "8px",
      border: "none",
      borderRadius: "5px",
      backgroundColor: selected ? c.selected : "transparent",
      color: selected ? c.link : itemink,
      font: "inherit",
      fontSize: 12.5,
      fontWeight: selected ? 600 : 500,
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { backgroundColor: selected ? c.selected : rowhover },
      "&:focus-visible": {
        outline: `2px solid ${action}`,
        outlineOffset: "-2px",
      },
    }),
    // The swatch is the palette's own background carrying its accent dot.
    paletteSwatch: (bg) => ({
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "14px",
      height: "14px",
      borderRadius: "4px",
      backgroundColor: bg,
      border: `1px solid ${c.border}`,
    }),
    paletteDot: (accent) => ({
      width: "5px",
      height: "5px",
      borderRadius: "999px",
      backgroundColor: accent,
    }),
    avatar: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
      borderRadius: "999px",
      backgroundColor: action,
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: 600,
    },
    userName: {
      fontSize: 13,
      fontWeight: 500,
      color: ink,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    userDomain: { fontSize: 11.5, color: heading, whiteSpace: "nowrap" },

    tooltip: {
      height: "28px",
      display: "flex",
      alignItems: "center",
      px: "10px",
      py: 0,
      m: 0,
      borderRadius: "6px",
      backgroundColor: ink,
      color: surface,
      fontSize: 12.5,
      fontWeight: 500,
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(16,24,40,.22)",
    },
  };
};

// Anchored off the icon box, so the offset clears the rest of the rail plus 8px.
const RailTooltip = ({ title, offset = 20, children }) => {
  const theme = useTheme();
  const s = styles(tokens(theme.paletteId));
  return (
    <Tooltip
      title={title}
      placement="right"
      enterDelay={400}
      enterNextDelay={0}
      componentsProps={{ tooltip: { sx: s.tooltip } }}
      PopperProps={{
        modifiers: [{ name: "offset", options: { offset: [0, offset] } }],
      }}
    >
      {children}
    </Tooltip>
  );
};

const Item = ({ title, tooltip, to, icon, active, collapsed, onClick }) => {
  const theme = useTheme();
  const s = styles(tokens(theme.paletteId));
  const item = (
    <Box
      {...(to
        ? { component: Link, to }
        : { component: "button", type: "button" })}
      onClick={onClick}
      aria-label={title}
      aria-current={active ? "page" : undefined}
      sx={s.item(active, collapsed)}
    >
      {icon}
      <Box component="span" sx={s.itemLabel(collapsed)}>
        {title}
      </Box>
    </Box>
  );

  if (!collapsed) return item;
  return <RailTooltip title={tooltip || title}>{item}</RailTooltip>;
};

const Sidebar = () => {
  const theme = useTheme();
  const s = styles(tokens(theme.paletteId));
  const loggedInUser = useContext(UserContext);
  const colorMode = useContext(ColorModeContext);

  // Appearance popup, anchored on the footer account row.
  const [menuOpen, setMenuOpen] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!footerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const { pathname } = useLocation();
  const isNotLocalBackend =
    sessionStorage.getItem("isNotLocalBackend") === "true";

  // Read the stored choice during the first render so the rail never snaps.
  const [collapsed, setCollapsed] = useState(
    () =>
      window.innerWidth < AUTO_COLLAPSE_BELOW ||
      Cache.get(COLLAPSE_KEY) === "true"
  );

  const toggle = useCallback(
    () =>
      setCollapsed((prev) => {
        Cache.set(COLLAPSE_KEY, String(!prev));
        return !prev;
      }),
    []
  );

  // Narrow viewports force the rail closed; widening restores what the user
  // last chose rather than whatever the narrow layout imposed. Only a crossing
  // of the threshold reacts, so resizing within a band leaves their toggle be.
  useEffect(() => {
    let wasNarrow = window.innerWidth < AUTO_COLLAPSE_BELOW;
    const onResize = () => {
      const isNarrow = window.innerWidth < AUTO_COLLAPSE_BELOW;
      if (isNarrow === wasNarrow) return;
      wasNarrow = isNarrow;
      setCollapsed(isNarrow ? true : Cache.get(COLLAPSE_KEY) === "true");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const canListPeople = useHasPermission(
    "/user-management/users",
    "GET",
    "LIST_PEOPLE"
  );
  const canListBots = useHasPermission(
    "/user-management/users",
    "GET",
    "LIST_BOTS"
  );

  const { IamModal, openIamModal, canViewIamModal } = useIamModal("");

  /**
   * @type {any[]}
   */
  const items = [
    {
      type: "group",
      title: "Control Planes",
    },
    {
      type: "item",
      title: "Dev/QA",
      to: "/",
      icon: <DesktopWindowsOutlined />,
    },
    {
      type: "item",
      title: "Stage",
      to: "/apps/tessell/deployments/stage/control-planes",
      icon: <LayersOutlined />,
    },
    {
      type: "item",
      title: "Prod",
      to: "/apps/tessell/deployments/prod/control-planes",
      icon: <PublicOutlined />,
    },
    {
      type: "group",
      title: "Releases",
    },
    (loggedInUser?.data?.roles?.includes(USER_ROLES.DEVOPS_TEAM) ||
      loggedInUser?.data?.roles?.includes(USER_ROLES.INFRA_TEAM)) && {
      type: "item",
      title: "Argo",
      to: "/argo",
      icon: <Argo />,
    },
    {
      type: "item",
      title: "Releases",
      to: "/releases",
      icon: <AltRouteOutlined />,
    },
    {
      type: "item",
      title: "Requests",
      // Collapsed, the headings are gone and this reads as "Pull Requests".
      tooltip: "Releases · Requests",
      to: "/requests",
      icon: <InboxOutlined />,
    },
    {
      type: "group",
      title: "Security & Cloud",
    },
    {
      type: "item",
      title: "Tessell GCP",
      to: "/apps/tessellgcp/deployments/dev/control-planes",
      // Highlight for every deployment stage (dev/qa/stage/prod), not just dev.
      activePrefix: "/apps/tessellgcp/deployments/",
      icon: <CloudOutlined />,
    },
    {
      type: "item",
      title: "Clusters",
      to: "/apps/tessell/deployment-stages/dev/k8s-clusters",
      // Highlight for every deployment stage (dev/qa/stage/prod), not just dev.
      activePrefix: "/apps/tessell/deployment-stages/",
      icon: <ViewInAr />,
    },
    {
      type: "item",
      title: "Vulnerabilities",
      to: "/vulnerabilities",
      icon: <ShieldOutlined />,
    },
    (canListPeople || canListBots) && {
      type: "item",
      title: "Users",
      to: "/users",
      icon: <GroupOutlined />,
    },
    {
      type: "group",
      title: "Software Image",
    },
    {
      type: "item",
      title: "DB Software",
      to: "/apps/tessell/software-images/db-software",
      icon: <DatabaseIcon />,
    },
    {
      type: "item",
      title: "Patch Builds",
      to: "/apps/tessell/software-images/db-software/builds",
      icon: <PackageIcon />,
    },
    {
      type: "group",
      title: "Reports",
    },
    {
      type: "item",
      title: "Automation Runs",
      tooltip: "Reports · Automation Runs",
      to: "/automations",
      // Also highlight on the tenant-scoped "/tenants/:tenantUrl/automation-runs" route.
      activeMatch: (path) =>
        path === "/automations" || path.endsWith("/automation-runs"),
      icon: <HistoryOutlined />,
    },
    {
      type: "item",
      title: "Code Coverage",
      tooltip: "Reports · Code Coverage",
      to: "/code-coverage",
      icon: <BarChartOutlined />,
    },
    {
      type: "item",
      title: "Pull Requests",
      tooltip: "Reports · Pull Requests",
      to: "/pull-requests",
      icon: <GitPullRequestIcon />,
    },
    {
      type: "item",
      title: "Logs",
      tooltip: "Reports · Logs",
      to: "/logs",
      icon: <DescriptionOutlined />,
    },
  ].filter(Boolean);

  const email = loggedInUser?.email || "";
  const [userName, userDomain] = email.split("@");
  const initials = (userName || "")
    .split(/[._-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const showBackendSwitch =
    window.location.origin.startsWith("http://localhost");

  return (
    <Box component="nav" aria-label="Main navigation" sx={s.sidebar(collapsed)}>
      {IamModal}
      <DrawerSnapToggle
        isWide={collapsed}
        onClick={toggle}
        ariaLabel="Expand sidebar"
        collapseLabel="Collapse sidebar"
        ariaExpanded={!collapsed}
        sx={s.toggle}
      />

      <Box sx={s.header(collapsed)}>
        <Box sx={s.logo}>
          <svg
            width="19"
            height="19"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.45"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 2.3l5.6 3L8 8.3 2.4 5.3z" />
            <path d="M2.4 8.2L8 11.2l5.6-3M2.4 10.9L8 13.9l5.6-3" />
          </svg>
        </Box>
        {collapsed ? null : (
          <Box sx={s.brandText}>
            <Typography sx={s.title}>Tessell Convoy</Typography>
            <Typography sx={s.subtitle}>DevOps Portal</Typography>
          </Box>
        )}
      </Box>

      <Box sx={s.nav(collapsed)}>
        {canViewIamModal ? (
          <Item
            title="IAM"
            icon={<VpnKey />}
            collapsed={collapsed}
            onClick={() => openIamModal()}
          />
        ) : null}
        {items.map((item, index) =>
          item.type === "item" ? (
            <Item
              key={index}
              title={item.title}
              tooltip={item.tooltip}
              to={item.to}
              icon={item.icon}
              collapsed={collapsed}
              active={
                item.activeMatch
                  ? item.activeMatch(pathname)
                  : item.activePrefix
                  ? pathname.startsWith(item.activePrefix)
                  : pathname === item.to
              }
            />
          ) : collapsed ? (
            <Box key={index} sx={s.groupRule} />
          ) : (
            <Typography key={index} sx={s.group}>
              {item.title}
            </Typography>
          )
        )}
        {showBackendSwitch ? (
          <Box sx={s.backendRow(collapsed)}>
            {collapsed ? (
              // Toggling blind from an icon is too easy to get wrong; open the
              // rail so the switch and its label are both visible first.
              <RailTooltip
                title={`Convoy-Dev backend · ${
                  isNotLocalBackend ? "on" : "off"
                }`}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={toggle}
                  aria-label={`Convoy-Dev backend is ${
                    isNotLocalBackend ? "on" : "off"
                  }. Expand sidebar to change it`}
                  sx={s.backendIcon}
                >
                  <ServerIcon />
                  <Box sx={s.backendDot(isNotLocalBackend)} />
                </Box>
              </RailTooltip>
            ) : (
              <>
                <Box component="span" sx={s.backendLabel}>
                  Convoy-Dev backend
                </Box>
                <Switch
                  size="small"
                  checked={isNotLocalBackend === true}
                  inputProps={{ "aria-label": "Use Convoy-Dev backend" }}
                  onChange={() => {
                    sessionStorage.setItem(
                      "isNotLocalBackend",
                      (!isNotLocalBackend).toString()
                    );
                    // Clear auth cache so the user re-authenticates
                    // against the newly selected backend (local ↔ dev).
                    // Without this, the stale session token from the
                    // previous backend is sent to the new backend, which
                    // doesn't recognise it in Redis and returns errors.
                    localStorage.clear();
                    window.location.reload();
                  }}
                />
              </>
            )}
          </Box>
        ) : null}
      </Box>

      {email ? (
        <Box sx={s.footer(collapsed)} ref={footerRef}>
          {collapsed ? (
            // The popup is not reachable from the icon-only rail; expand first,
            // matching how the Convoy-Dev backend switch already behaves.
            <RailTooltip title={email} offset={24}>
              <Box sx={s.avatar} aria-label={email}>
                {initials}
              </Box>
            </RailTooltip>
          ) : (
            <>
              <Box
                component="button"
                type="button"
                sx={s.accountButton(menuOpen)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account and appearance"
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <Box sx={s.avatar}>{initials}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={s.userName}>{userName}</Typography>
                  <Typography sx={s.userDomain}>@{userDomain}</Typography>
                </Box>
                <ExpandLess sx={s.accountChevron(menuOpen)} />
              </Box>

              {menuOpen ? (
                <Box sx={s.menuPopup} role="menu">
                  <Typography sx={s.menuOverline}>Appearance</Typography>

                  {MODE_OPTIONS.map(({ id, label, Icon }) => (
                    <React.Fragment key={id}>
                      <Box
                        component="button"
                        type="button"
                        role="menuitemradio"
                        aria-checked={colorMode.mode === id}
                        sx={s.menuRow(colorMode.mode === id)}
                        onClick={() => colorMode.setMode(id)}
                      >
                        <Icon sx={s.menuRowIcon} />
                        {label}
                        {colorMode.mode === id ? (
                          <Check sx={s.menuRowCheck} />
                        ) : null}
                      </Box>

                      {colorMode.mode === id &&
                      colorMode.resolvedMode === "dark" ? (
                        <Box sx={s.paletteWrap}>
                          <Typography sx={s.paletteOverline}>
                            Dark palette
                          </Typography>
                          {DARK_PALETTES.map((p) => (
                            <Box
                              key={p.id}
                              component="button"
                              type="button"
                              role="menuitemradio"
                              aria-checked={colorMode.palette === p.id}
                              sx={s.paletteRow(colorMode.palette === p.id)}
                              onClick={() => colorMode.setPalette(p.id)}
                            >
                              <Box sx={s.paletteSwatch(p.swatch)}>
                                <Box sx={s.paletteDot(p.accent)} />
                              </Box>
                              {p.label}
                              {colorMode.palette === p.id ? (
                                <Check sx={s.menuRowCheck} />
                              ) : null}
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </React.Fragment>
                  ))}
                </Box>
              ) : null}
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default Sidebar;
